import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { AlertTriangle, Trash2, RefreshCcw } from 'lucide-react';
import { toast } from 'sonner';
import { orphanAPI, restaurantsAPI } from '../lib/api';

/**
 * Modal that lists placements whose linked restaurant has been deleted.
 * Admin can either reassign each orphan to an existing restaurant or delete it.
 * Props:
 *   open            — controlled visibility
 *   onOpenChange    — controlled close handler
 *   onResolved      — called after any successful repair (parent should refresh)
 */
const OrphanPlacementsDialog = ({ open, onOpenChange, onResolved }) => {
  const [orphans, setOrphans] = useState([]);
  const [restaurants, setRestaurants] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectionByOrphan, setSelectionByOrphan] = useState({}); // { [placementId]: restaurantId }
  const [busy, setBusy] = useState(null); // placementId being acted on

  useEffect(() => {
    if (!open) return;
    reload();
  }, [open]);

  const reload = async () => {
    setLoading(true);
    try {
      const [oRes, rRes] = await Promise.all([orphanAPI.listPlacements(), restaurantsAPI.getAll()]);
      setOrphans(oRes.data || []);
      setRestaurants((rRes.data || []).slice().sort((a, b) => a.name.localeCompare(b.name)));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load orphaned placements');
    } finally {
      setLoading(false);
    }
  };

  const handleReassign = async (orphan) => {
    const rid = selectionByOrphan[orphan.id];
    if (!rid) { toast.error('Choose a restaurant to reassign to'); return; }
    setBusy(orphan.id);
    try {
      await orphanAPI.reassignPlacement(orphan.id, rid);
      toast.success(`Reassigned ${orphan.student_name}'s placement`);
      await reload();
      onResolved && onResolved();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Reassign failed');
    } finally {
      setBusy(null);
    }
  };

  const handleDelete = async (orphan) => {
    if (!window.confirm(`Delete the orphaned placement for ${orphan.student_name} (${orphan.position || 'no position'})? This cannot be undone.`)) return;
    setBusy(orphan.id);
    try {
      await orphanAPI.deletePlacement(orphan.id);
      toast.success('Orphaned placement deleted');
      await reload();
      onResolved && onResolved();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Delete failed');
    } finally {
      setBusy(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col" data-testid="orphan-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-amber-700">
            <AlertTriangle className="w-5 h-5" />
            Repair Orphaned Placements
          </DialogTitle>
          <DialogDescription>
            The restaurant these placements were linked to has been deleted. Reassign each to an existing
            restaurant, or delete the orphaned record. Pending feedback forms attached to a deleted orphan
            will also be removed.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-auto">
          {loading ? (
            <div className="py-10 text-center text-gray-500">Loading…</div>
          ) : orphans.length === 0 ? (
            <div className="py-10 text-center text-emerald-700 bg-emerald-50 rounded-md" data-testid="orphan-empty">
              🎉 No orphaned placements. Everything is linked cleanly.
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="text-xs text-gray-500 uppercase border-b border-gray-200">
                <tr>
                  <th className="text-left py-2 px-3">Student</th>
                  <th className="text-left py-2 px-3">Position / Period</th>
                  <th className="text-left py-2 px-3">Deleted restaurant</th>
                  <th className="text-left py-2 px-3 w-[42%]">Action</th>
                </tr>
              </thead>
              <tbody>
                {orphans.map((o) => (
                  <tr key={o.id} className="border-b border-gray-100" data-testid={`orphan-row-${o.id}`}>
                    <td className="py-3 px-3 align-top">
                      <div className="font-medium text-ica-navy">{o.student_name}</div>
                      <div className="text-xs text-gray-500">
                        {o.student_number || '—'} · {o.student_year || '—'}
                      </div>
                    </td>
                    <td className="py-3 px-3 align-top">
                      <div className="text-gray-800">{o.position || '—'}</div>
                      <div className="text-xs text-gray-500">
                        {o.start_date || '—'} → {o.end_date || '—'} · <span className="uppercase">{o.status || '—'}</span>
                      </div>
                    </td>
                    <td className="py-3 px-3 align-top">
                      <div className="text-gray-700">{o.restaurant_name_snapshot || <em className="text-gray-400">(name lost)</em>}</div>
                      <div className="text-[11px] text-gray-400 font-mono break-all">{o.restaurant_id}</div>
                    </td>
                    <td className="py-3 px-3 align-top">
                      <div className="flex flex-wrap items-center gap-2">
                        <select
                          data-testid={`orphan-reassign-select-${o.id}`}
                          value={selectionByOrphan[o.id] || ''}
                          onChange={(e) => setSelectionByOrphan(prev => ({ ...prev, [o.id]: e.target.value }))}
                          className="rounded-md border border-gray-300 px-2 py-1.5 text-sm focus:border-ica-navy focus:ring-1 focus:ring-ica-navy flex-1 min-w-[180px]"
                        >
                          <option value="">Reassign to…</option>
                          {restaurants.map((r) => (
                            <option key={r.id} value={r.id}>{r.name}</option>
                          ))}
                        </select>
                        <Button
                          data-testid={`orphan-reassign-btn-${o.id}`}
                          size="sm"
                          disabled={busy === o.id || !selectionByOrphan[o.id]}
                          onClick={() => handleReassign(o)}
                          
                        >
                          <RefreshCcw className="w-3.5 h-3.5 mr-1" /> Reassign
                        </Button>
                        <Button
                          data-testid={`orphan-delete-btn-${o.id}`}
                          size="sm"
                          variant="ghost"
                          disabled={busy === o.id}
                          onClick={() => handleDelete(o)}
                          className="text-red-600 hover:bg-red-50"
                        >
                          <Trash2 className="w-3.5 h-3.5 mr-1" /> Delete
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default OrphanPlacementsDialog;
